import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, ChevronRight, Minus, Plus, ShoppingBag, Heart, 
  Share2, Truck, MapPin, Shield, Check, Star, Package,
  Ruler, Info, AlertCircle, Loader2, Crown
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CostumeDetails() {
  const { costumeId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : false;
  const [costume, setCostume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [shippingRates, setShippingRates] = useState({});
  
  // Checkout form state
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [shippingMethod, setShippingMethod] = useState('standard');
  const [isLocalPickup, setIsLocalPickup] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadCostume();
    loadShippingRates();
  }, [costumeId]);

  const loadCostume = async () => {
    try {
      const response = await axios.get(`${API}/costumes/${costumeId}`);
      setCostume(response.data);
      
      // Set default selections
      if (response.data.sizes_available?.length > 0) {
        setSelectedSize(response.data.sizes_available[0]);
      }
      if (response.data.colors_available?.length > 0) {
        setSelectedColor(response.data.colors_available[0]);
      }
    } catch (error) {
      console.error('Error loading costume:', error);
      toast.error('Costume not found');
      navigate('/costume-shop');
    } finally {
      setLoading(false);
    }
  };

  const loadShippingRates = async () => {
    try {
      const response = await axios.get(`${API}/costumes/shipping-rates`);
      setShippingRates(response.data);
    } catch (error) {
      console.error('Error loading shipping rates:', error);
    }
  };

  const calculateTotal = () => {
    if (!costume) return 0;
    let total = costume.base_price * quantity;
    
    // Add shipping if not local pickup
    if (!isLocalPickup && shippingRates[shippingMethod]) {
      total += shippingRates[shippingMethod].price;
    }
    
    return total;
  };

  const handleCheckout = async () => {
    if (!customerEmail || !customerEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!customerName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (costume.sizes_available?.length > 0 && !selectedSize) {
      toast.error('Please select a size');
      return;
    }

    setIsCheckingOut(true);

    try {
      const response = await axios.post(`${API}/costumes/checkout`, {
        customer_email: customerEmail,
        customer_name: customerName,
        items: [{
          costume_id: costume.id,
          size: selectedSize || 'One Size',
          color: selectedColor || null,
          quantity: quantity
        }],
        shipping_method: isLocalPickup ? 'local_pickup' : shippingMethod,
        is_local_pickup: isLocalPickup,
        notes: notes || null
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setIsCheckingOut(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: costume.name,
          text: `Check out this costume: ${costume.name}`,
          url: url
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="costume-details-loading">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!costume) {
    return null;
  }

  // Prefer the 1920px WebP display derivatives on detail pages — originals stay available via `images`.
  const images = costume.images_display?.length > 0
    ? costume.images_display
    : (costume.images?.length > 0 ? costume.images : [costume.thumbnail || '/api/placeholder/600/800']);

  return (
    <div className="min-h-screen pb-20" data-testid="costume-details-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <nav className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/costume-shop" className="hover:text-primary transition-colors">
            Costume Shop
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-foreground">{costume.name}</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <motion.div
              key={selectedImage}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-stone-800"
            >
              <img loading="lazy" decoding="async"
                src={images[selectedImage]}
                alt={costume.name}
                className="w-full h-full object-cover"
                data-testid="main-image"
              />
              
              {/* Navigation Arrows */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setSelectedImage((prev) => (prev === 0 ? images.length - 1 : prev - 1))}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 backdrop-blur-sm rounded-full hover:bg-black/70 transition-colors"
                    data-testid="prev-image-btn"
                  >
                    <ChevronLeft className="w-6 h-6 text-white" />
                  </button>
                  <button
                    onClick={() => setSelectedImage((prev) => (prev === images.length - 1 ? 0 : prev + 1))}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 backdrop-blur-sm rounded-full hover:bg-black/70 transition-colors"
                    data-testid="next-image-btn"
                  >
                    <ChevronRight className="w-6 h-6 text-white" />
                  </button>
                </>
              )}

              {/* Image Counter */}
              {images.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/50 backdrop-blur-sm rounded-full text-white text-sm">
                  {selectedImage + 1} / {images.length}
                </div>
              )}
            </motion.div>

            {/* Thumbnail Strip */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`flex-shrink-0 w-20 h-24 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === idx ? 'border-primary' : 'border-transparent hover:border-primary/50'
                    }`}
                    data-testid={`thumbnail-${idx}`}
                  >
                    <img loading="lazy" decoding="async" src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            {/* Category & Featured Badge */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-primary uppercase tracking-wider">
                {costume.category}
              </span>
              {costume.featured && (
                <span className="flex items-center gap-1 px-2 py-1 bg-primary/20 text-primary rounded-full text-xs font-bold">
                  <Star className="w-3 h-3" />
                  Featured
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold font-display text-foreground" data-testid="costume-title">
              {costume.name}
            </h1>

            {/* Price — hidden for admin */}
            <div className="flex items-baseline gap-3">
              {isAdminUser ? (
                <span className="text-2xl font-bold text-yellow-400 font-display">Included (Admin)</span>
              ) : (
                <>
                  <span className="text-3xl font-bold text-primary font-display" data-testid="costume-price">
                    ${costume.base_price?.toFixed(2)}
                  </span>
                  {costume.is_custom_order && (
                    <span className="text-sm text-muted-foreground">(Starting price)</span>
                  )}
                </>
              )}
            </div>

            {/* Description */}
            <p className="text-muted-foreground leading-relaxed" data-testid="costume-description">
              {costume.description}
            </p>

            {/* Size Selection */}
            {costume.sizes_available?.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-foreground">
                    Select Size
                  </label>
                  <button className="flex items-center gap-1 text-xs text-primary hover:underline">
                    <Ruler className="w-3 h-3" />
                    Size Guide
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {costume.sizes_available.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`min-w-[48px] px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                        selectedSize === size
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-stone-800 text-muted-foreground hover:bg-stone-700 hover:text-foreground'
                      }`}
                      data-testid={`size-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Selection */}
            {costume.colors_available?.length > 0 && (
              <div className="space-y-3">
                <label className="text-sm font-semibold text-foreground">
                  Select Color
                </label>
                <div className="flex flex-wrap gap-2">
                  {costume.colors_available.map((color) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                        selectedColor === color
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-stone-800 text-muted-foreground hover:bg-stone-700 hover:text-foreground'
                      }`}
                      data-testid={`color-${color}`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="space-y-3">
              <label className="text-sm font-semibold text-foreground">Quantity</label>
              <div className="flex items-center gap-4">
                <div className="flex items-center bg-stone-800 rounded-lg">
                  <button
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="p-3 text-muted-foreground hover:text-foreground transition-colors"
                    disabled={quantity <= 1}
                    data-testid="quantity-decrease"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-12 text-center font-bold text-foreground" data-testid="quantity-value">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity((q) => q + 1)}
                    className="p-3 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="quantity-increase"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Shipping Options */}
            <div className="space-y-3 p-4 bg-stone-900/50 rounded-xl border border-border/50">
              <label className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Truck className="w-4 h-4 text-primary" />
                Shipping Options
              </label>
              
              {/* Local Pickup Toggle */}
              <label className="flex items-center gap-3 cursor-pointer group p-3 rounded-lg hover:bg-stone-800/50 transition-colors">
                <input
                  type="checkbox"
                  checked={isLocalPickup}
                  onChange={(e) => setIsLocalPickup(e.target.checked)}
                  className="w-5 h-5 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                  data-testid="local-pickup-checkbox"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-foreground font-medium">
                    <MapPin className="w-4 h-4 text-green-400" />
                    Local Pickup (New Orleans)
                  </div>
                  <p className="text-xs text-muted-foreground">Free - Same day pickup available</p>
                </div>
                <span className="text-green-400 font-bold">FREE</span>
              </label>

              {/* Shipping Methods */}
              <AnimatePresence>
                {!isLocalPickup && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-2 overflow-hidden"
                  >
                    {Object.entries(shippingRates).filter(([key]) => key !== 'local_pickup').map(([key, rate]) => (
                      <label
                        key={key}
                        className={`flex items-center gap-3 cursor-pointer p-3 rounded-lg border transition-all ${
                          shippingMethod === key
                            ? 'border-primary bg-primary/10'
                            : 'border-border/50 hover:border-primary/50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="shipping"
                          value={key}
                          checked={shippingMethod === key}
                          onChange={() => setShippingMethod(key)}
                          className="w-4 h-4 text-primary focus:ring-2 focus:ring-primary"
                          data-testid={`shipping-${key}`}
                        />
                        <div className="flex-1">
                          <span className="text-foreground font-medium">{rate.name}</span>
                          <p className="text-xs text-muted-foreground">{rate.days}</p>
                        </div>
                        <span className="font-bold text-foreground">${rate.price.toFixed(2)}</span>
                      </label>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Admin trial banner */}
            {isAdminUser && (
              <div className="p-4 rounded-2xl border-2 border-yellow-500/50 bg-gradient-to-r from-yellow-500/10 to-amber-500/10 mb-3">
                <div className="flex items-start gap-3">
                  <Crown className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-bold text-yellow-300 text-sm">Admin Order — Free Sample</h3>
                    <p className="text-xs text-yellow-100/80 mt-1">
                      Skip Stripe and place this order at $0 to verify the confirmation email, order record, and fulfillment flow.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!selectedSize || !selectedColor) {
                        toast.error('Pick a size + color first');
                        return;
                      }
                      try {
                        setIsCheckingOut(true);
                        const orderId = `admin-costume-${Date.now()}`;
                        const email = 'admin@casasamba.com';
                        await axios.post(`${API}/orders`, {
                          id: orderId,
                          type: 'admin_free',
                          item_type: 'costume',
                          item_id: costumeId,
                          item_title: costume?.title || costume?.name || 'Costume',
                          size: selectedSize,
                          color: selectedColor,
                          quantity,
                          customer_email: email,
                          amount: 0,
                          list_price: calculateTotal(),
                          status: 'confirmed',
                        }).catch(() => null);
                        toast.success('Admin free order recorded — flow verified end-to-end');
                        navigate(`/costume-order-confirmation?order_id=${orderId}&admin=1`);
                      } finally {
                        setIsCheckingOut(false);
                      }
                    }}
                    disabled={isCheckingOut}
                    data-testid="admin-free-costume-order-btn"
                    className="px-4 py-2 rounded-lg font-bold bg-yellow-500 hover:bg-yellow-400 text-black text-sm transition-colors disabled:opacity-50"
                  >
                    Free Order
                  </button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowCheckoutForm(true)}
                className="flex-1 flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-bold text-lg hover:bg-primary/90 transition-all hover:scale-105 active:scale-95 shadow-[0_0_20px_-5px_rgba(245,158,11,0.4)]"
                data-testid="buy-now-btn"
              >
                <ShoppingBag className="w-5 h-5" />
                Buy Now {isAdminUser ? '(Free — Admin)' : `- $${calculateTotal().toFixed(2)}`}
              </button>
              
              <button
                onClick={() => {
                  setIsFavorite(!isFavorite);
                  toast.success(isFavorite ? 'Removed from favorites' : 'Added to favorites');
                }}
                className={`p-4 rounded-full border transition-all ${
                  isFavorite 
                    ? 'bg-red-500/20 border-red-500 text-red-500'
                    : 'border-border hover:border-primary text-muted-foreground hover:text-primary'
                }`}
                data-testid="favorite-btn"
              >
                <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
              </button>
              
              <button
                onClick={handleShare}
                className="p-4 rounded-full border border-border hover:border-primary text-muted-foreground hover:text-primary transition-all"
                data-testid="share-btn"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-border">
              <div className="text-center">
                <Shield className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">Secure Checkout</p>
              </div>
              <div className="text-center">
                <Package className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">Quality Guarantee</p>
              </div>
              <div className="text-center">
                <Truck className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">{costume.estimated_delivery_days}-Day Delivery</p>
              </div>
            </div>

            {/* Custom Order Note */}
            {costume.is_custom_order && (
              <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                <Info className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground">Custom Order Item</p>
                  <p className="text-xs text-muted-foreground">
                    This costume requires custom measurements. After purchase, we'll contact you for sizing details.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Checkout Modal */}
      <AnimatePresence>
        {showCheckoutForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => !isCheckingOut && setShowCheckoutForm(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-lg bg-card border border-border rounded-2xl p-6 overflow-y-auto max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
              data-testid="checkout-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold font-display text-foreground">Complete Your Order</h2>
                <button
                  onClick={() => setShowCheckoutForm(false)}
                  disabled={isCheckingOut}
                  className="p-2 hover:bg-stone-800 rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5 rotate-45" />
                </button>
              </div>

              {/* Order Summary */}
              <div className="p-4 bg-stone-900/50 rounded-xl mb-6">
                <div className="flex gap-4">
                  <img loading="lazy" decoding="async"
                    src={costume.thumbnail || costume.images_thumb?.[0] || costume.images?.[0]}
                    alt={costume.name}
                    className="w-20 h-24 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground">{costume.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {selectedSize && `Size: ${selectedSize}`}
                      {selectedColor && ` • Color: ${selectedColor}`}
                    </p>
                    <p className="text-sm text-muted-foreground">Qty: {quantity}</p>
                  </div>
                  <span className="font-bold text-primary">${(costume.base_price * quantity).toFixed(2)}</span>
                </div>
                
                {!isLocalPickup && shippingRates[shippingMethod] && (
                  <div className="flex justify-between mt-3 pt-3 border-t border-border/50 text-sm">
                    <span className="text-muted-foreground">{shippingRates[shippingMethod].name}</span>
                    <span className="text-foreground">${shippingRates[shippingMethod].price.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="flex justify-between mt-3 pt-3 border-t border-border text-lg font-bold">
                  <span className="text-foreground">Total</span>
                  <span className="text-primary">${calculateTotal().toFixed(2)}</span>
                </div>
              </div>

              {/* Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    disabled={isCheckingOut}
                    data-testid="checkout-email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Your full name"
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    disabled={isCheckingOut}
                    data-testid="checkout-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Order Notes (Optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Special instructions or measurements..."
                    rows={3}
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    disabled={isCheckingOut}
                    data-testid="checkout-notes"
                  />
                </div>

                {/* Delivery Method Summary */}
                <div className="flex items-center gap-3 p-3 bg-stone-800/50 rounded-xl">
                  {isLocalPickup ? (
                    <>
                      <MapPin className="w-5 h-5 text-green-400" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Local Pickup</p>
                        <p className="text-xs text-muted-foreground">800 Race Street, New Orleans, LA 70130</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <Truck className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-sm font-medium text-foreground">{shippingRates[shippingMethod]?.name}</p>
                        <p className="text-xs text-muted-foreground">Shipping address collected at checkout</p>
                      </div>
                    </>
                  )}
                </div>

                <button
                  onClick={handleCheckout}
                  disabled={isCheckingOut}
                  className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-bold text-lg hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="proceed-checkout-btn"
                >
                  {isCheckingOut ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Shield className="w-5 h-5" />
                      Proceed to Secure Checkout
                    </>
                  )}
                </button>

                <p className="text-xs text-center text-muted-foreground">
                  You'll be redirected to Stripe for secure payment
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
